import React, { useState } from "react";
import axios from "axios";
import { Facebook, Instagram, Youtube, MessageCircle, Phone, Mail, MapPin, Plane, Send } from "lucide-react";
import { CONTACT, DESTINATIONS, SERVICES } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter a valid email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're subscribed! Watch your inbox for travel deals.");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Subscription failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#0A2E6F] text-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#F9B233]">
                <Plane className="w-5 h-5 text-[#0B3D91]" strokeWidth={2.5} />
              </span>
              <span className="text-xl font-bold">Malviya <span className="text-[#F9B233]">Travel</span></span>
            </div>
            <p className="mt-4 text-sm text-white/75 leading-relaxed max-w-md">
              Your trusted travel partner for every journey. We craft memorable domestic and international
              holidays with personalized service, transparent pricing, and 24/7 support.
            </p>
            <form onSubmit={subscribe} data-testid="newsletter-form" className="mt-6 flex flex-col sm:flex-row gap-2 max-w-md">
              <Input
                data-testid="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Get travel deals in your inbox"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:border-[#F9B233] h-11 rounded-full px-5"
              />
              <Button
                type="submit"
                data-testid="newsletter-submit"
                disabled={loading}
                className="bg-[#F9B233] hover:bg-[#e6a11f] text-[#0B3D91] font-semibold rounded-full h-11 px-5 whitespace-nowrap"
              >
                <Send className="w-4 h-4 mr-1.5" /> {loading ? "..." : "Subscribe"}
              </Button>
            </form>
            <div className="mt-6 flex items-center gap-3">
              {[
                { icon: Facebook, href: "https://facebook.com", label: "Facebook" },
                { icon: Instagram, href: "https://instagram.com", label: "Instagram" },
                { icon: MessageCircle, href: `https://wa.me/${CONTACT.whatsapp}`, label: "WhatsApp" },
                { icon: Youtube, href: "https://youtube.com", label: "Youtube" },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  data-testid={`social-${s.label.toLowerCase()}`}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#F9B233] hover:text-[#0B3D91] text-white flex items-center justify-center transition-colors"
                >
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-bold tracking-widest uppercase text-[#F9B233]">Quick Links</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              {[
                { l: "Home", h: "#home" }, { l: "About", h: "#about" }, { l: "Packages", h: "#packages" },
                { l: "Services", h: "#services" }, { l: "Gallery", h: "#gallery" }, { l: "FAQ", h: "#faq" }, { l: "Contact", h: "#contact" }
              ].map(x => (
                <li key={x.l}>
                  <a href={x.h} onClick={(e) => { e.preventDefault(); scrollTo(x.h); }} className="text-white/80 hover:text-[#F9B233] transition-colors">{x.l}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Destinations */}
          <div>
            <h4 className="text-sm font-bold tracking-widest uppercase text-[#F9B233]">Destinations</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              {DESTINATIONS.slice(0, 7).map(d => (
                <li key={d.name}>
                  <a href="#destinations" onClick={(e) => { e.preventDefault(); scrollTo("#destinations"); }} className="text-white/80 hover:text-[#F9B233] transition-colors">{d.name}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-bold tracking-widest uppercase text-[#F9B233]">Contact</h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <Phone className="w-4 h-4 mt-0.5 text-[#F9B233]" />
                <a href={`tel:${CONTACT.phone.replace(/\s/g,'')}`} className="text-white/85 hover:text-white">{CONTACT.phone}</a>
              </li>
              <li className="flex items-start gap-2.5">
                <Mail className="w-4 h-4 mt-0.5 text-[#F9B233]" />
                <a href={`mailto:${CONTACT.email}`} className="text-white/85 hover:text-white break-all">{CONTACT.email}</a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 text-[#F9B233]" />
                <span className="text-white/85">{CONTACT.address}</span>
              </li>
            </ul>
            <h4 className="mt-6 text-sm font-bold tracking-widest uppercase text-[#F9B233]">Popular Services</h4>
            <ul className="mt-3 space-y-2 text-sm">
              {SERVICES.slice(0, 4).map(s => (
                <li key={s.title} className="text-white/80">{s.title}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-white/70">
          <p data-testid="footer-copyright">© 2026 Malviya Travel. All Rights Reserved.</p>
          <p>Crafted with care for wanderlust travelers.</p>
        </div>
      </div>
    </footer>
  );
}
